// Entry point: boots PixiJS (WebGL, pixel-perfect), wires the fixed-timestep loop,
// input, sim (World), renderer (Stage), HUD and the level-up draft. See plan §4.

import { Application, TextureStyle } from 'pixi.js';
import { GameLoop } from './core/loop';
import { Input } from './core/input';
import { World } from './game/world';
import { Stage } from './render/stage';
import { Hud } from './ui/hud';
import { DraftUI } from './ui/draft';
import { Menu } from './ui/menu';
import { GameOverUI } from './ui/gameover';
import { StartUI } from './ui/start';
import { TouchJoystick } from './ui/touchJoystick';
import { LeaderboardClient } from './net/leaderboardClient';
import { Music } from './audio/music';
import { makeIconCanvas, SPEAKER_OFF, SPEAKER_ON } from './ui/icons';
import { isCompactTouchLayout } from './ui/platform';
import { ZOOM } from './game/balance';
import type { DifficultyKey } from './game/balance';
import {
  BASE_LAB_LEVEL,
  difficultyModeForLevel,
  getLevelDefinition,
  isLevelUnlocked,
} from './game/levels';
import type { LevelDefinition } from './game/levels';
import { ABILITIES } from './game/activeAbilities';
import { VARIANT } from './game/variant';
import { applyHiddenLabels } from './game/hiddenLabels';
import { resolveHiddenArenaLevel, shouldForcePrivateAbilities } from './game/hiddenArena';
import {
  announceBridgePaused,
  announceBridgeRun,
  initNativeBridge,
  isNativeShell,
  requestNativeExit,
  requestNativeHaptic,
  requestNativeLeaderboard,
  requestNativeRestart,
  requestNativeScoreShare,
  requestNativeSettingsUpdate,
  sendGameOver,
  sendWebError,
  type Entitlements,
  type NativeSettings,
  type RunConfig,
} from './bridge/nativeBridge';

// Math.random is fine here (outside the deterministic sim) - it just seeds the run.
const makeSeed = (): number => (Math.random() * 0xffffffff) >>> 0;

async function main(): Promise<void> {
  const searchParams = new URLSearchParams(location.search);
  const debugMode = searchParams.has('debug');
  const hiddenVariant = VARIANT === 'hidden';
  const nativeShell = isNativeShell();
  let browserDebugLevel = BASE_LAB_LEVEL;
  const privateExpansionLevelId = hiddenVariant
    ? resolveHiddenArenaLevel(location.pathname, searchParams)
    : null;
  const forcePrivateAbilities = shouldForcePrivateAbilities(hiddenVariant, privateExpansionLevelId);
  if (privateExpansionLevelId !== null && !nativeShell) {
    browserDebugLevel = getLevelDefinition(privateExpansionLevelId);
  } else if (debugMode && !nativeShell) {
    const requestedLevelId = searchParams.get('level');
    if (requestedLevelId !== null) {
      try {
        browserDebugLevel = getLevelDefinition(requestedLevelId);
      } catch {
        console.warn(`[Brewmancer debug] Unknown level: ${requestedLevelId}`);
      }
    }
  }

  // Must be set BEFORE any texture loads or sprites come out blurry (plan §9-A).
  TextureStyle.defaultOptions.scaleMode = 'nearest';

  // Hidden variant: rename weapons/passives before any UI reads their defs.
  if (VARIANT === 'hidden') applyHiddenLabels();

  const app = new Application();
  await app.init({
    preference: 'webgl', // WebGPU isn't Safari-safe yet (plan §1)
    antialias: false, // pixel art + a v8 perf bug make AA undesirable
    roundPixels: true,
    background: BASE_LAB_LEVEL.visualTheme.backgroundColor,
    resolution: window.devicePixelRatio || 1,
    autoDensity: true,
    resizeTo: window,
    autoStart: false, // we drive rendering from our own fixed-timestep loop
  });

  const mount = document.getElementById('app');
  if (!mount) throw new Error('#app mount point not found');
  mount.appendChild(app.canvas);
  app.stage.eventMode = 'none';

  app.resize();

  const stage = new Stage(app);
  app.stage.addChild(stage.root);
  const input = new Input(
    window,
    ABILITIES.map((a) => a.hotkey),
  );
  const touchJoystick = new TouchJoystick(mount);
  input.attachTouch(app.canvas, touchJoystick); // joystick on the canvas only, so DOM UI taps still work
  const hud = new Hud(mount, (i) => {
    const def = world.abilityDefs[i];
    if (def !== undefined) input.pressAbility(def.hotkey);
  });
  const draftUI = new DraftUI(mount);
  const leaderboard = new LeaderboardClient({
    native: nativeShell,
    onNativeLeaderboard: requestNativeLeaderboard,
  });
  const gameOverUI = new GameOverUI(
    mount,
    restart,
    nativeShell ? requestNativeExit : undefined,
    nativeShell
      ? (share) =>
          requestNativeScoreShare({
            score: share.score,
            seconds: share.seconds,
            playerLevel: share.level,
            victory: share.victory,
          })
      : undefined,
  );
  const music = new Music();
  app.canvas.addEventListener('pointerdown', () => music.resume(), { passive: true });

  // Difficulty (chosen on the start screen, persisted; reused across restarts).
  let difficulty: DifficultyKey = 'normal';
  try {
    const saved = localStorage.getItem('bm_difficulty');
    if (saved === 'easy' || saved === 'normal') difficulty = saved;
  } catch {
    /* ignore */
  }
  let secretMode = forcePrivateAbilities; // private expansion routes always start fully unlocked

  let currentSeed = makeSeed();
  let currentLevel = nativeShell ? BASE_LAB_LEVEL : browserDebugLevel;
  let world = new World(currentSeed, currentLevel);
  world.difficulty = difficultyModeForLevel(currentLevel, difficulty);
  world.allAbilities = secretMode;
  let manualPause = false;
  let menuOpen = false;
  let wasGameOver = false;
  let nativeStartedRunId: string | null = null;
  let nativeSettings: NativeSettings | null = null;
  let nativeEntitlements: Entitlements = { premium: false };

  // Keep the render zoom + targeting viewport in sync with the screen (plan §9-F). On
  // phones, zoom the world out so more of the arena is visible (UI elements are unaffected).
  const applyView = (): void => {
    const phone = Math.min(window.innerWidth, window.innerHeight) < 560;
    const zoom = phone ? ZOOM * 0.7 : ZOOM;
    stage.setZoom(zoom);
    world.setView(app.screen.width / zoom / 2, app.screen.height / zoom / 2);
  };
  window.addEventListener('resize', () => {
    app.resize();
    applyView();
  });
  applyView();

  const loop = new GameLoop({
    update: (dt) => {
      world.update(dt, input);
      world.flushHapticEvents((event) => {
        if (event === 'damageHeavy') music.playPlayerHit();
        if (nativeShell) requestNativeHaptic(event);
      });
    },
    render: (alpha) => {
      // ESC / P: close the menu if open, else toggle pause (ignored during a draft).
      if (input.consumePause()) {
        if (menu.isOpen()) menu.hide();
        else if (!draftUI.visible) {
          manualPause = !manualPause;
          loop.paused = manualPause || menuOpen;
        }
      }
      // Gamepad Menu/Start button → toggle the pause menu (like clicking MENU).
      if (input.consumePadMenu() && !draftUI.visible) menu.toggle();
      // Open the level-up draft when one is pending (not while the menu is open).
      if (!draftUI.visible && !menuOpen && !world.gameOver && world.pendingLevelUps > 0)
        openDraft();

      // On death/victory, show the game-over screen once (with score + leaderboard submit).
      if (world.gameOver && !wasGameOver) {
        wasGameOver = true;
        sendGameOver({ world, debug: debugMode, hiddenVariant });
        gameOverUI.show(
          {
            score: world.score(),
            kills: world.kills,
            level: world.level,
            seconds: world.elapsedSec,
            victory: world.victory,
            ranked: world.difficulty.ranked && !world.allAbilities && VARIANT !== 'hidden',
          },
          leaderboard,
        );
      }

      stage.sync(world, alpha);
      app.renderer.render(app.stage);
      hud.update(world, manualPause);
    },
  });

  // Native lifecycle pauses stay paused until the player explicitly taps Resume.
  hud.setResumeHandler(() => {
    if (!manualPause) return;
    manualPause = false;
    loop.paused = menuOpen || draftUI.visible;
    music.resume();
  });

  // Pause menu (top-right button) + leaderboard pop-out. Opening pauses; closing resumes.
  const menu = new Menu(
    mount,
    (open) => {
      menuOpen = open;
      loop.paused = open || manualPause;
    },
    leaderboard,
    () => world.levelDefinition,
    nativeShell ? requestNativeExit : undefined,
  );

  function openDraft(): void {
    loop.paused = true;
    draftUI.show(world.rollDraft(), onPick);
  }
  function onPick(choice: Parameters<typeof world.applyChoice>[0]): void {
    world.applyChoice(choice);
    draftUI.hide();
    if (world.pendingLevelUps > 0) openDraft();
    else loop.paused = manualPause || menuOpen;
  }

  function resetRun(
    seed: number,
    runDifficulty: DifficultyKey,
    allAbilities: boolean,
    level: LevelDefinition = BASE_LAB_LEVEL,
  ): void {
    currentSeed = seed;
    currentLevel = level;
    difficulty = runDifficulty;
    music.setTrack(currentLevel.musicTrack);
    world = new World(currentSeed, currentLevel);
    app.renderer.background.color = currentLevel.visualTheme.backgroundColor;
    world.difficulty = difficultyModeForLevel(currentLevel, difficulty);
    world.allAbilities = allAbilities;
    if (!nativeShell) void leaderboard.startSession();
    applyView();
    menu.hide();
    draftUI.hide();
    gameOverUI.hide();
    manualPause = false;
    menuOpen = false;
    wasGameOver = false;
    loop.paused = false;
  }

  function startNativeRun(run: RunConfig): void {
    let level: LevelDefinition;
    try {
      level = getLevelDefinition(run.levelId);
    } catch {
      sendWebError(`Unsupported iOS level: ${run.levelId}`);
      return;
    }
    if (!isLevelUnlocked(level, nativeEntitlements.premium)) {
      sendWebError(`Locked iOS level: ${run.levelId}`);
      return;
    }
    if (nativeStartedRunId === run.runId) return;

    nativeStartedRunId = run.runId;
    secretMode = false;
    resetRun(run.seed, run.difficulty, false, level);
    announceBridgeRun(run, { debug: debugMode, allAbilities: false, hiddenVariant });
    music.start();
  }

  // Restart: shared by the R key and game-over Restart. Native runs must receive a
  // fresh RunConfig from Expo; browser runs remain fully local.
  function restart(): void {
    if (nativeShell) {
      requestNativeRestart();
      return;
    }
    resetRun(makeSeed(), difficulty, secretMode, currentLevel);
  }
  window.addEventListener('keydown', (e) => {
    // Don't treat typing in the name field (game-over) as a restart.
    if (e.target instanceof HTMLInputElement || e.target instanceof HTMLSelectElement) return;
    if (e.code === 'KeyR' && world.gameOver) restart();
  });

  // Start paused behind either the browser start UI or the native bootstrap.
  loop.paused = true;
  if (!nativeShell) {
    new StartUI(mount, (chosen, secret) => {
      secretMode = secret || forcePrivateAbilities;
      const selectedDifficulty = secretMode ? 'easy' : chosen; // private runs stay unranked
      resetRun(currentSeed, selectedDifficulty, secretMode, browserDebugLevel);
      music.start(); // the START click is the user gesture that unlocks audio
    });
  }

  // Audio mute toggle (bottom-left) + the M key.
  const muteBtn = document.createElement('button');
  const compactControls = isCompactTouchLayout();
  const syncMute = (): void => {
    muteBtn.replaceChildren(
      makeIconCanvas(
        music.isMuted ? SPEAKER_OFF : SPEAKER_ON,
        music.isMuted ? 0x8a8597 : 0xe8e4f0,
        2,
      ),
    );
  };
  const applyNativeSettings = (settings: NativeSettings): void => {
    nativeSettings = settings;
    music.setMuted(!settings.musicEnabled);
    syncMute();
  };
  Object.assign(muteBtn.style, {
    position: 'fixed',
    left: '10px',
    bottom: compactControls ? 'calc(22px + env(safe-area-inset-bottom, 0px))' : '52px',
    zIndex: '50',
    width: '38px',
    height: '38px',
    display: 'grid',
    placeItems: 'center',
    borderRadius: '8px',
    border: '1px solid #4a4366',
    background: 'rgba(16,12,24,0.85)',
    cursor: 'pointer',
  });
  muteBtn.title = 'Audio (M)';
  syncMute();
  const toggleMusic = (): void => {
    music.toggleMute();
    syncMute();
    if (nativeSettings !== null) {
      nativeSettings = { ...nativeSettings, musicEnabled: !music.isMuted };
      requestNativeSettingsUpdate(nativeSettings);
    }
  };
  muteBtn.addEventListener('click', toggleMusic);
  document.body.appendChild(muteBtn);
  window.addEventListener('keydown', (e) => {
    if (
      e.code === 'KeyM' &&
      !(e.target instanceof HTMLInputElement) &&
      !(e.target instanceof HTMLSelectElement)
    ) {
      toggleMusic();
    }
  });
  // WebKit lifecycle events are the local fallback if the React Native bridge is
  // suspended before its native.pause injection gets a chance to execute.
  const pauseForLifecycle = (): void => {
    if (world.gameOver) return;
    manualPause = true;
    loop.paused = true;
    music.pause();
    announceBridgePaused();
  };
  document.addEventListener('visibilitychange', () => {
    if (document.hidden) pauseForLifecycle();
    else if (!manualPause) music.resume();
  });
  window.addEventListener('pagehide', pauseForLifecycle);
  window.addEventListener('blur', pauseForLifecycle);

  loop.start();

  // Register native callbacks before web.ready. Expo owns the run configuration and
  // sends the one bootstrap that starts an iOS session; browsers keep the StartUI above.
  initNativeBridge(
    { debug: debugMode, hiddenVariant },
    {
      onBootstrap: ({ run, settings, entitlements }) => {
        nativeEntitlements = entitlements;
        applyNativeSettings(settings);
        startNativeRun(run);
        // Music can be created while the run starts. Reapply the native preference
        // after that creation so it is authoritative from the first audible frame.
        applyNativeSettings(settings);
      },
      onStartRun: (run) => {
        startNativeRun(run);
        if (nativeSettings !== null) applyNativeSettings(nativeSettings);
      },
      onPause: pauseForLifecycle,
      onResume: () => {
        loop.paused = manualPause || menuOpen || draftUI.visible;
      },
      onSettingsUpdated: (settings) => {
        applyNativeSettings(settings);
      },
      onEntitlementsUpdated: (entitlements) => {
        nativeEntitlements = entitlements;
      },
      onScoreResult: (result) => {
        console.log('[Brewmancer bridge] native.scoreResult', result);
      },
    },
  );

  // Debug harness (plan §7): ?debug=1 exposes hooks to step the sim and grant upgrades.
  if (debugMode) {
    Object.assign(window, {
      __alkahest: {
        get world(): World {
          return world;
        },
        app,
        stage,
        input,
        loop,
        tick(n = 1): void {
          for (let i = 0; i < n; i++) world.update(1 / 60, input);
        },
        render(): void {
          stage.sync(world, 1);
          app.renderer.render(app.stage);
          hud.update(world, manualPause);
        },
        grant(key: string): void {
          world.weapons.add(key);
        },
        setLevel(n: number): void {
          world.level = n;
        },
        cast(i: number): void {
          world.abilities.activate(i, world.level, world);
        },
        showDraft(): void {
          draftUI.show(world.rollDraft(), (c) => {
            world.applyChoice(c);
            draftUI.hide();
          });
        },
        unlockAll(): void {
          world.level = 40;
          for (let i = 0; i < world.abilities.cd.length; i++) world.abilities.cd[i] = 0;
          world.player.hp = world.player.maxHp;
        },
        endRun(): void {
          if (!world.gameOver) world.player.hp = 0;
        },
      },
    });
    // Press U to unlock every ability (+ reset cooldowns and heal) for testing.
    window.addEventListener('keydown', (e) => {
      if (e.code === 'KeyU') {
        world.level = 40;
        for (let i = 0; i < world.abilities.cd.length; i++) world.abilities.cd[i] = 0;
        world.player.hp = world.player.maxHp;
      }
      if (e.code === 'KeyG') world.triggerGravityRails(1);
      if (e.code === 'KeyB') world.triggerBloomPulse(3);
    });
  }
}

main().catch((err: unknown) => {
  console.error(err);
  sendWebError(err);
  document.body.innerHTML = `<pre style="color:#ff8888;padding:1rem;white-space:pre-wrap">Failed to start Brewmancer:\n${String(err)}</pre>`;
});
